/**
 * 빛의 방 (Lumina Room) — 부트스트랩.
 * 모듈을 조립하고 렌더 루프를 시작한다. 초기화 실패는 #err 패널로 노출한다.
 */
import * as THREE from 'three';
import { assertWebGLAvailable } from './core/capabilities';
import { createRenderer } from './core/renderer';
import { createStage } from './core/stage';
import { Loop } from './core/Loop';
import { Room } from './room/Room';
import { DustField } from './world/DustField';
import { PaintingLibrary } from './paintings/PaintingLibrary';
import { ShowDirector } from './show/ShowDirector';
import { AudioBed } from './audio/AudioBed';
import { DesktopControls } from './controls/DesktopControls';
import { setupVRButton } from './xr/XRSession';
import { setupControllers } from './xr/XRControllers';
import { getHUD } from './ui/hud';
import { showError } from './ui/errorOverlay';

try {
  assertWebGLAvailable();

  const { enterOverlay, vrButton } = getHUD();
  const renderer = createRenderer();
  const { scene, dolly, camera } = createStage();

  // 월드
  const library = new PaintingLibrary();
  const room = new Room();
  scene.add(room.group);

  const dust = new DustField();
  scene.add(dust.points);

  // BGM 베드 (자동재생 정책 때문에 제스처에서 시작)
  const listener = new THREE.AudioListener();
  camera.add(listener);
  const audio = new AudioBed(listener);

  // 쇼: 6면 명화 시퀀스 → 피날레 → 암전 → 정전 → 반복 (BGM 게인도 함께 구동)
  const show = new ShowDirector(room, dust, library, audio);

  // 컨트롤 & XR
  const desktopControls = new DesktopControls(renderer, camera, dolly, enterOverlay);
  setupControllers(renderer, dolly);
  setupVRButton(renderer, vrButton, showError);

  // 사용자 제스처에서 BGM 시작 (입장 클릭 / 첫 키입력 / VR 진입)
  const startAudio = () => audio.start();
  enterOverlay.addEventListener('click', startAudio);
  window.addEventListener('keydown', startAudio, { once: true });
  renderer.xr.addEventListener('sessionstart', startAudio);

  // 렌더 루프
  const loop = new Loop(renderer, scene, camera);
  loop.add((dt) => room.update(dt));
  loop.add((dt) => show.update(dt));
  loop.add((dt) => dust.update(dt));
  loop.add((dt) => desktopControls.update(dt));
  loop.start();

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  // 개발 전용 디버그 핸들 — 콘솔에서 window.lumina로 씬/쇼를 만질 수 있다.
  // 프로덕션 빌드에선 import.meta.env.DEV가 false라 이 블록이 통째로 제거된다.
  if (import.meta.env.DEV) {
    (window as unknown as { lumina: unknown }).lumina = {
      renderer, scene, camera, dolly, room, dust, show, library, audio,
    };
  }
} catch (error) {
  const e = error as Error;
  showError(e.message + (e.stack ? `\n\n${e.stack}` : ''));
}
